import JSZip from 'jszip';
import {
  BATCH_TRANSFER_MAX_IMPORT_BYTES,
  COMBINED_PACKAGE_MAX_BYTES,
  COMBINED_PACKAGE_MAX_CORRECTION_BYTES,
  parseBatchIdentificationResults,
  parseChecklistCorrectionsFile,
  type BatchIdentificationResults,
  type ChecklistCorrectionsFile,
} from '@tcvs/shared';

const RESULTS_NAME = 'identification-results.json';
const CORRECTIONS_NAME = 'checklist-corrections.json';
export class CombinedPackageFileError extends Error {
  constructor(
    message: string,
    readonly reason:
      | 'INVALID_JSON'
      | 'INVALID_FORMAT'
      | 'FILE_TOO_LARGE'
      | 'WRONG_BATCH'
      | 'FILE_READ_FAILED',
  ) {
    super(message);
  }
}
const reject = (
  message: string,
  reason: CombinedPackageFileError['reason'] = 'INVALID_FORMAT',
): never => {
  throw new CombinedPackageFileError(message, reason);
};
const limitFor = (name: string) =>
  name === CORRECTIONS_NAME
    ? COMBINED_PACKAGE_MAX_CORRECTION_BYTES
    : BATCH_TRANSFER_MAX_IMPORT_BYTES;
const allowedName = (name: string) => name === RESULTS_NAME || name === CORRECTIONS_NAME;
function read(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, failure) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () =>
      failure(
        new CombinedPackageFileError('The package file could not be read.', 'FILE_READ_FAILED'),
      );
    reader.readAsArrayBuffer(file);
  });
}
interface ZipEntry {
  name: string;
  bytes: number;
  crc: number;
}
/** Inspect original ZIP names before JSZip normalizes paths or overwrites duplicate entries. */
function inspectZip(buffer: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buffer);
  const data = new Uint8Array(buffer);
  const decoder = new TextDecoder('utf-8', { fatal: true });
  let end = -1;
  for (let i = data.length - 22; i >= Math.max(0, data.length - 65557); --i) {
    if (
      view.getUint32(i, true) === 0x06054b50 &&
      i + 22 + view.getUint16(i + 20, true) === data.length
    ) {
      end = i;
      break;
    }
  }
  if (end < 0) return reject('The ZIP directory is invalid.');
  const count = view.getUint16(end + 10, true);
  const size = view.getUint32(end + 12, true);
  const start = view.getUint32(end + 16, true);
  if (
    view.getUint16(end + 4, true) !== 0 ||
    view.getUint16(end + 6, true) !== 0 ||
    view.getUint16(end + 8, true) !== count ||
    count < 1 ||
    count > 2 ||
    start + size !== end
  )
    return reject(
      'Use a ZIP containing only the identification results and optional checklist corrections.',
    );
  const names = new Set<string>();
  const entries: ZipEntry[] = [];
  let offset = start;
  let total = 0;
  for (let i = 0; i < count; ++i) {
    if (offset + 46 > end || view.getUint32(offset, true) !== 0x02014b50)
      return reject('The ZIP directory is invalid.');
    const flags = view.getUint16(offset + 8, true);
    const method = view.getUint16(offset + 10, true);
    const compressed = view.getUint32(offset + 20, true);
    const bytes = view.getUint32(offset + 24, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const local = view.getUint32(offset + 42, true);
    if (offset + 46 + nameLength + extraLength + commentLength > end)
      return reject('The ZIP directory is invalid.');
    const name = decoder.decode(data.subarray(offset + 46, offset + 46 + nameLength));
    if (!allowedName(name) || names.has(name))
      return reject(
        'ZIP files must have unique, exact root names: identification-results.json and optional checklist-corrections.json. Folders and product files are not supported.',
      );
    if (
      flags & 1 ||
      ![0, 8].includes(method) ||
      view.getUint16(offset + 34, true) !== 0 ||
      local + 30 > start ||
      view.getUint32(local, true) !== 0x04034b50
    )
      return reject('Encrypted, split or unsupported ZIP files are not supported.');
    const localNameLength = view.getUint16(local + 26, true);
    const localExtra = view.getUint16(local + 28, true);
    const payload = local + 30 + localNameLength + localExtra;
    if (
      payload + compressed > start ||
      view.getUint16(local + 6, true) !== flags ||
      view.getUint16(local + 8, true) !== method ||
      decoder.decode(data.subarray(local + 30, local + 30 + localNameLength)) !== name
    )
      return reject('The ZIP file headers disagree.');
    total += bytes;
    if (bytes > limitFor(name) || total > COMBINED_PACKAGE_MAX_BYTES)
      return reject(
        'Expanded package data must be at most 2 MiB total; checklist corrections must be at most 512 KiB.',
        'FILE_TOO_LARGE',
      );
    names.add(name);
    entries.push({ name, bytes, crc: view.getUint32(offset + 16, true) });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  if (offset !== end || !names.has(RESULTS_NAME))
    return reject('The package must contain identification-results.json.');
  return entries;
}
function boundedEntry(entry: JSZip.JSZipObject, descriptor: ZipEntry): Promise<Uint8Array> {
  return new Promise((resolve, failure) => {
    const chunks: Uint8Array[] = [];
    let length = 0;
    let crc = -1;
    let failed = false;
    // JSZip 3.10 exposes internalStream in the browser, but omits it from its .d.ts.
    const stream = (
      entry as unknown as {
        internalStream(type: 'uint8array'): {
          on(event: 'data', callback: (chunk: Uint8Array) => void): void;
          on(event: 'error', callback: (error: Error) => void): void;
          on(event: 'end', callback: () => void): void;
          pause(): void;
          resume(): void;
        };
      }
    ).internalStream('uint8array');
    stream.on('data', (chunk: Uint8Array) => {
      if (failed) return;
      length += chunk.length;
      if (length > descriptor.bytes || length > limitFor(descriptor.name)) {
        failed = true;
        stream.pause();
        failure(
          new CombinedPackageFileError(
            'The ZIP expanded beyond its declared size.',
            'FILE_TOO_LARGE',
          ),
        );
        return;
      }
      for (const byte of chunk) {
        crc ^= byte;
        for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
      }
      chunks.push(chunk);
    });
    stream.on('error', failure);
    stream.on('end', () => {
      if (failed) return;
      if (length !== descriptor.bytes || (crc ^ -1) >>> 0 !== descriptor.crc) {
        failure(
          new CombinedPackageFileError(
            'The ZIP content failed its integrity check.',
            'INVALID_FORMAT',
          ),
        );
        return;
      }
      const combined = new Uint8Array(length);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }
      resolve(combined);
    });
    stream.resume();
  });
}
export interface CombinedPackageFiles {
  results: BatchIdentificationResults;
  corrections?: ChecklistCorrectionsFile;
}
export async function readCombinedPackageFiles(
  files: File[],
  batchId: string,
): Promise<CombinedPackageFiles> {
  if (files.length < 1 || files.length > 2)
    return reject(
      'Choose one package ZIP or identification-results.json and optional checklist-corrections.json.',
    );
  if (files.reduce((n, file) => n + file.size, 0) > COMBINED_PACKAGE_MAX_BYTES)
    return reject('The package must be at most 2 MiB.', 'FILE_TOO_LARGE');
  const contents = new Map<string, Uint8Array>();
  try {
    if (files.length === 1 && files[0]!.name.toLowerCase().endsWith('.zip')) {
      const buffer = await read(files[0]!);
      const descriptors = inspectZip(buffer);
      const zip = await JSZip.loadAsync(buffer);
      for (const descriptor of descriptors) {
        const entry = zip.file(descriptor.name);
        if (!entry) return reject('A required ZIP entry is unavailable.');
        contents.set(descriptor.name, await boundedEntry(entry, descriptor));
      }
    } else {
      for (const file of files) {
        if (!allowedName(file.name) || contents.has(file.name))
          return reject(
            'Choose uniquely named identification-results.json and optional checklist-corrections.json. Product files are not supported.',
          );
        if (file.size > limitFor(file.name))
          return reject('Checklist corrections must be at most 512 KiB.', 'FILE_TOO_LARGE');
        contents.set(file.name, new Uint8Array(await read(file)));
      }
    }
    if (!contents.has(RESULTS_NAME))
      return reject('The package must contain identification-results.json.');
    const parseJson = (bytes: Uint8Array): unknown => {
      try {
        return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
      } catch {
        return reject('A package file is not valid UTF-8 JSON.', 'INVALID_JSON');
      }
    };
    const results = parseBatchIdentificationResults(parseJson(contents.get(RESULTS_NAME)!));
    if (!results.ok) return reject(results.error);
    if (results.value.batchId !== batchId)
      return reject('This result file belongs to a different batch.', 'WRONG_BATCH');
    const correctionBytes = contents.get(CORRECTIONS_NAME);
    if (!correctionBytes) return { results: results.value };
    const corrections = parseChecklistCorrectionsFile(parseJson(correctionBytes));
    if (!corrections.ok) return reject(corrections.error);
    if (
      corrections.value.batchId !== batchId ||
      corrections.value.exportId !== results.value.exportId
    )
      return reject(
        'Checklist corrections and identification results must belong to the same batch and export.',
        'WRONG_BATCH',
      );
    return { results: results.value, corrections: corrections.value };
  } catch (cause) {
    if (cause instanceof CombinedPackageFileError) throw cause;
    return reject('The package could not be read. Check the ZIP format and JSON files.');
  }
}
